import { useState } from "react";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const languages = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
];

interface LanguageSelectorProps {
  variant?: "icon" | "full";
  className?: string;
}

const LanguageSelector = ({ variant = "icon", className = "" }: LanguageSelectorProps) => {
  const [currentLang, setCurrentLang] = useState("en");
  const currentLanguage = languages.find((l) => l.code === currentLang);

  const handleLanguageChange = (langCode: string) => {
    setCurrentLang(langCode);
    // In a real app, this would trigger i18n language change
    console.log(`Language changed to: ${langCode}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "icon" ? (
          <Button
            variant="ghost"
            size="icon"
            className={`h-9 w-9 text-muted-foreground hover:text-primary ${className}`}
          >
            <span className="text-lg leading-none">{currentLanguage?.flag}</span>
            <span className="sr-only">Select language</span>
          </Button>
        ) : (
          <button
            className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-foreground/70 hover:text-primary hover:bg-primary/5 transition-colors w-full ${className}`}
          >
            <Globe className="h-4 w-4" />
            <span>{currentLanguage?.flag} {currentLanguage?.name}</span>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover z-50 min-w-[150px]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer ${
              currentLang === lang.code ? "bg-primary/10 text-primary" : ""
            }`}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
